import { format, getMonth, getYear, setMonth, setYear } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  label: React.ReactNode;
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabledDates?: (date: Date) => boolean;
  errorMessage?: string;
  startYear?: number;
  endYear?: number;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  selectedDate,
  onDateChange,
  disabledDates,
  errorMessage,
  startYear = getYear(new Date()) - 125,
  endYear = getYear(new Date()) + 125
}) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  const handleMonthChange = (month: string) => {
    if (!selectedDate) return;
    const newDate = setMonth(selectedDate, months.indexOf(month));
    onDateChange(newDate);
  };

  const handleYearChange = (year: string) => {
    if (!selectedDate) return;
    const newDate = setYear(selectedDate, parseInt(year));
    onDateChange(newDate);
  };

  const currentMonth = selectedDate ? months[getMonth(selectedDate)] : months[getMonth(new Date())];
  const currentYear = selectedDate
    ? getYear(selectedDate).toString()
    : getYear(new Date()).toString();

  return (
    <FormItem className="flex flex-col">
      <FormLabel>{label}</FormLabel>
      <Popover>
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              variant={'outline'}
              className={cn(
                'w-full pl-3 text-left font-normal',
                !selectedDate && 'text-muted-foreground'
              )}
            >
              {selectedDate ? format(selectedDate, 'PPP') : <span>Pick a date</span>}
              <CalendarIcon className="w-4 h-4 ml-auto opacity-50" />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex justify-between p-2">
            <Select onValueChange={handleMonthChange} value={currentMonth}>
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select onValueChange={handleYearChange} value={currentYear}>
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onDateChange}
            disabled={disabledDates}
            initialFocus
            month={selectedDate || new Date()}
          />
        </PopoverContent>
      </Popover>
      {errorMessage && <FormMessage className="text-xs">{errorMessage}</FormMessage>}
    </FormItem>
  );
};
